import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { errorText, listFrom, scoreText } from "./appraisalUtils";

const { Text, Title } = Typography;
const maxFor = (criterion) => {
  const raw = criterion?.maxRating ?? criterion?.max_rating;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 20;
};
const savedMaxFor = (item, criteriaList, savedScore) => {
  const direct =
    item?.maxRating ??
    item?.max_rating ??
    item?.criterion?.maxRating ??
    item?.criterion?.max_rating;
  const parsed = Number(direct);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  const itemId = String(
    item?.criterionId ?? item?.criterion?.id ?? item?.id,
  );
  const snapshot =
    savedScore?.includedCriteria ?? savedScore?.included_criteria ?? [];
  const snapshotted = (Array.isArray(snapshot) ? snapshot : []).find(
    (entry) =>
      String(entry?.id ?? entry?.criterionId ?? entry?.criterion_id) ===
      itemId,
  );
  const snapshotMax = Number(
    snapshotted?.max_rating ?? snapshotted?.maxRating,
  );
  if (Number.isFinite(snapshotMax) && snapshotMax > 0) return snapshotMax;
  const match = (criteriaList || []).find(
    (criterion) => String(criterion.id) === itemId,
  );
  return maxFor(match);
};
const activeCriteria = (data) =>
  listFrom(data).filter((item) => item?.isActive);

const AppraisalFiveR = ({
  record,
  cycleId,
  request,
  canManage = false,
  cycleLocked = false,
  onChanged,
}) => {
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState([]);
  const [saved, setSaved] = useState(record?.fiveR || null);
  const [latestRecordStatus, setLatestRecordStatus] = useState(record?.status);
  const [latestCycleLocked, setLatestCycleLocked] = useState(cycleLocked);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [catalogNotice, setCatalogNotice] = useState("");
  const mountedRef = useRef(false);
  const writeLockRef = useRef(false);
  const loadSequenceRef = useRef(0);
  const draftRef = useRef({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadSequenceRef.current += 1;
    };
  }, []);

  useEffect(() => {
    setSaved(record?.fiveR || null);
    setLatestRecordStatus(record?.status);
  }, [record]);

  useEffect(() => {
    setLatestCycleLocked(cycleLocked);
  }, [cycleLocked]);

  const mayEdit =
    canManage &&
    latestRecordStatus === "in_progress" &&
    !saved?.isLocked &&
    !cycleLocked &&
    !latestCycleLocked &&
    !!cycleId &&
    !refreshRequired;

  const getLatestEditable = async () => {
    const [latestRecordResponse, latestCycleResponse] = await Promise.all([
      request("GET", `records/${record.id}`),
      cycleId ? request("GET", `cycles/${cycleId}`) : Promise.resolve(null),
    ]);
    const latestRecord = latestRecordResponse?.record || latestRecordResponse;
    const latestCycle = latestCycleResponse?.cycle || latestCycleResponse;
    if (!mountedRef.current) return false;
    const fiveR = latestRecord?.fiveR || null;
    setSaved(fiveR);
    setLatestRecordStatus(latestRecord?.status);
    setLatestCycleLocked(latestCycle?.status === "Locked");
    const editable =
      canManage &&
      latestRecord?.status === "in_progress" &&
      !fiveR?.isLocked &&
      !!cycleId &&
      latestCycle?.status !== "Locked";
    if (!editable && mountedRef.current)
      setError("This appraisal or its cycle is no longer editable.");
    return editable;
  };

  const preserveDraft = (nextCriteria) => {
    const current = form.getFieldsValue(true);
    const previous = draftRef.current || {};
    const ratings = { ...(previous.ratings || {}), ...(current.ratings || {}) };
    const validIds = new Set(nextCriteria.map((item) => String(item.id)));
    const filtered = Object.fromEntries(
      Object.entries(ratings).filter(([id]) => validIds.has(String(id))),
    );
    draftRef.current = {
      ...previous,
      ...current,
      ratings: filtered,
    };
    form.setFieldsValue(draftRef.current);
  };

  const fetchCatalog = async () => {
    const data = await request("GET", "criteria", undefined, {
      isActive: true,
    });
    const nextCriteria = activeCriteria(data);
    if (mountedRef.current) {
      setCriteria(nextCriteria);
      preserveDraft(nextCriteria);
    }
    return nextCriteria;
  };

  const openEditor = async () => {
    if (!mayEdit || writeLockRef.current || loading) return;
    const seq = ++loadSequenceRef.current;
    setLoading(true);
    setError("");
    setCatalogNotice("");
    try {
      const [currentCriteria, freshRecord, freshCycle] = await Promise.all([
        fetchCatalog(),
        request("GET", `records/${record.id}`),
        cycleId ? request("GET", `cycles/${cycleId}`) : Promise.resolve(null),
      ]);
      if (!mountedRef.current || seq !== loadSequenceRef.current) return;
      const fresh = freshRecord?.record || freshRecord;
      const cycle = freshCycle?.cycle || freshCycle;
      const latestFiveR = fresh?.fiveR || null;
      setSaved(latestFiveR);
      setLatestRecordStatus(fresh?.status);
      setLatestCycleLocked(cycle?.status === "Locked");
      const latestEditable =
        canManage &&
        fresh?.status === "in_progress" &&
        !latestFiveR?.isLocked &&
        !cycleLocked &&
        !!cycleId &&
        cycle?.status !== "Locked";
      if (!latestEditable) {
        setError("This appraisal can no longer be edited.");
        return;
      }
      const priorDraft = draftRef.current || {};
      const byId = Object.fromEntries(
        (latestFiveR?.ratings || []).map((item) => [
          String(item.criterionId ?? item.criterion?.id ?? item.id),
          { rating: item.rating, comment: item.comment || "" },
        ]),
      );
      const validIds = new Set(currentCriteria.map((item) => String(item.id)));
      const ratings = Object.fromEntries(
        Object.entries({ ...byId, ...(priorDraft.ratings || {}) }).filter(
          ([id]) => validIds.has(String(id)),
        ),
      );
      const draft = {
        ratings,
        overallComment:
          priorDraft.overallComment ?? latestFiveR?.overallComment ?? "",
      };
      draftRef.current = draft;
      form.resetFields();
      form.setFieldsValue(draft);
      setEditorOpen(true);
    } catch (loadError) {
      if (mountedRef.current && seq === loadSequenceRef.current)
        setError(errorText(loadError));
    } finally {
      if (mountedRef.current && seq === loadSequenceRef.current)
        setLoading(false);
    }
  };

  const closeEditor = () => {
    if (writeLockRef.current) return;
    setEditorOpen(false);
    setError("");
    setCatalogNotice("");
  };

  const refreshCatalogAfterError = async () => {
    try {
      const next = activeCriteria(
        await request("GET", "criteria", undefined, { isActive: true }),
      );
      if (!mountedRef.current) return;
      setCriteria(next);
      preserveDraft(next);
      setCatalogNotice(
        "Active criteria were refreshed. Review the updated form and submit again if appropriate.",
      );
    } catch (refreshError) {
      if (mountedRef.current)
        setCatalogNotice(
          `Criteria could not be refreshed: ${errorText(refreshError)}`,
        );
    }
  };

  const save = async () => {
    if (!mayEdit || writeLockRef.current || !editorOpen) return;
    if (!criteria.length) {
      setError(
        "There are no active 5R criteria, so this assessment cannot be submitted.",
      );
      return;
    }
    writeLockRef.current = true;
    setSaving(true);
    setError("");
    setCatalogNotice("");
    try {
      const values = await form.validateFields();
      if (!mountedRef.current) return;
      if (!(await getLatestEditable())) return;
      const payload = {
        appraisalRecordId: record.id,
        ratings: criteria.map((criterion) => ({
          criterionId: criterion.id,
          rating: values.ratings?.[criterion.id]?.rating,
          comment: values.ratings?.[criterion.id]?.comment || "",
        })),
        overallComment: values.overallComment || "",
      };
      draftRef.current = values;
      try {
        const response = await request("POST", "fiveR", payload);
        if (!mountedRef.current) return;
        setSaved(response?.fiveR || response || null);
        form.resetFields();
        draftRef.current = {};
      } catch (writeError) {
        if (mountedRef.current) setError(errorText(writeError));
        if (
          writeError?.response?.status === 400 ||
          writeError?.status === 400
        ) {
          await refreshCatalogAfterError();
          try {
            await getLatestEditable();
          } catch {
            /* Keep the original submit error visible. */
          }
          if (mountedRef.current) setError(errorText(writeError));
        }
        return;
      }
      setEditorOpen(false);
      let refreshSucceeded = true;
      try {
        if (typeof onChanged === "function")
          refreshSucceeded = (await onChanged()) !== false;
      } catch (_) {
        refreshSucceeded = false;
      }
      if (!refreshSucceeded && mountedRef.current) {
        setRefreshRequired(true);
        setError(
          "Assessment saved, but the appraisal record could not be refreshed. Reload the drawer before making another change.",
        );
      }
    } catch (validationError) {
      if (!validationError?.errorFields && mountedRef.current)
        setError(errorText(validationError));
    } finally {
      writeLockRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const remove = async () => {
    if (!mayEdit || writeLockRef.current || !saved?.id) return;
    writeLockRef.current = true;
    setSaving(true);
    setError("");
    try {
      if (!(await getLatestEditable())) return;
      await request("DELETE", `fiveR/${saved.id}`);
      if (!mountedRef.current) return;
      setSaved(null);
      form.resetFields();
      draftRef.current = {};
      let refreshSucceeded = true;
      try {
        if (typeof onChanged === "function")
          refreshSucceeded = (await onChanged()) !== false;
      } catch (_) {
        refreshSucceeded = false;
      }
      if (!refreshSucceeded && mountedRef.current) {
        setRefreshRequired(true);
        setError(
          "Assessment deleted, but the appraisal record could not be refreshed. Reload the drawer to confirm its status.",
        );
      }
    } catch (deleteError) {
      if (mountedRef.current) setError(errorText(deleteError));
    } finally {
      writeLockRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  return (
    <Card
      title={
        <Title level={5} style={{ margin: 0 }}>
          5R annual assessment
        </Title>
      }
      extra={
        mayEdit ? (
          <Space>
            {saved?.id && (
              <Popconfirm
                title="Delete this 5R assessment?"
                description="This permanently removes the saved annual assessment."
                okText="Delete"
                cancelText="Cancel"
                onConfirm={remove}
                disabled={saving}
              >
                <Button danger icon={<DeleteOutlined />} disabled={saving}>
                  Delete
                </Button>
              </Popconfirm>
            )}
            <Button
              type="primary"
              icon={saved ? <EditOutlined /> : <PlusOutlined />}
              onClick={openEditor}
              loading={loading}
              disabled={saving}
            >
              {saved ? "Edit assessment" : "Complete assessment"}
            </Button>
          </Space>
        ) : null
      }
    >
      {error && !editorOpen && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      {(refreshRequired || (canManage && !mayEdit)) && (
        <Button
          loading={loading}
          onClick={async () => {
            if (writeLockRef.current || loading) return;
            setLoading(true);
            try {
              const editable = await getLatestEditable();
              if ((await onChanged?.()) === false) return;
              if (mountedRef.current) {
                setRefreshRequired(false);
                if (editable) setError("");
              }
            } catch (refreshError) {
              if (mountedRef.current) setError(errorText(refreshError));
            } finally {
              if (mountedRef.current) setLoading(false);
            }
          }}
        >
          Refresh appraisal
        </Button>
      )}
      {saved ? (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Space wrap>
            <Tag color={saved.isLocked ? "default" : "blue"}>
              {saved.isLocked ? "Locked" : "Saved"}
            </Tag>
            <Text>
              <strong>Average:</strong> {scoreText(saved.averageRating)}
              {saved.averageRating != null ? " / 5" : ""}
            </Text>
            <Text>
              <strong>Supervisor:</strong> {saved.supervisorName || "—"}
            </Text>
            <Text>
              <strong>Submitted:</strong> {saved.submittedAt || "—"}
            </Text>
          </Space>
          <Text type="secondary">
            {saved.staffName || "—"}
            {saved.staffCategoryDisplay
              ? ` · ${saved.staffCategoryDisplay}`
              : ""}
          </Text>
          {(saved.ratings || []).length ? (
            saved.ratings.map((item, index) => {
              const max = savedMaxFor(item, criteria, saved);
              return (
                <Card
                  key={item.criterionId ?? item.id ?? index}
                  size="small"
                  title={
                    item.criterionName || item.criterion?.name || "Criterion"
                  }
                >
                  <Text>
                    <strong>Rating:</strong>{" "}
                    {item.rating != null
                      ? `${item.rating}/${max}`
                      : "Not scored"}
                  </Text>
                  {item.comment ? (
                    <div style={{ marginTop: 8 }}>{item.comment}</div>
                  ) : null}
                </Card>
              );
            })
          ) : (
            <Text type="secondary">No criterion ratings were returned.</Text>
          )}
          <div>
            <Text strong>Overall comment</Text>
            <div>{saved.overallComment || "—"}</div>
          </div>
        </Space>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No 5R assessment has been saved for this appraisal."
        />
      )}

      <Modal
        open={editorOpen}
        title={
          saved ? "Edit 5R annual assessment" : "Complete 5R annual assessment"
        }
        okText="Save assessment"
        onOk={save}
        onCancel={closeEditor}
        confirmLoading={saving}
        okButtonProps={{ disabled: !mayEdit || !criteria.length || loading }}
        cancelButtonProps={{ disabled: saving }}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
        destroyOnClose
      >
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 12 }}
          />
        )}
        {catalogNotice && (
          <Alert
            type="info"
            showIcon
            message={catalogNotice}
            style={{ marginBottom: 12 }}
          />
        )}
        {!criteria.length ? (
          <Alert
            type="warning"
            showIcon
            message="There are no active 5R criteria. An assessment cannot be submitted until criteria are configured."
          />
        ) : (
          <Form
            form={form}
            layout="vertical"
            disabled={saving || !mayEdit}
            onValuesChange={(_, values) => {
              draftRef.current = values;
            }}
          >
            {criteria.map((criterion) => {
              const max = maxFor(criterion);
              return (
                <Card
                  key={criterion.id}
                  size="small"
                  title={criterion.name}
                  style={{ marginBottom: 12 }}
                >
                  {criterion.description && (
                    <Text type="secondary">{criterion.description}</Text>
                  )}
                  <Form.Item
                    name={["ratings", String(criterion.id), "rating"]}
                    label={`Rating (0 to ${max})`}
                    rules={[
                      {
                        required: true,
                        message: `Select a rating from 0 to ${max}.`,
                      },
                      {
                        type: "integer",
                        min: 0,
                        max,
                        message: `Enter a whole number from 0 to ${max}.`,
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={max}
                      precision={0}
                      step={1}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={["ratings", String(criterion.id), "comment"]}
                    label="Comment (optional)"
                    style={{ marginBottom: 0 }}
                  >
                    <Input.TextArea rows={2} maxLength={2000} showCount />
                  </Form.Item>
                </Card>
              );
            })}
            <Form.Item name="overallComment" label="Overall comment (optional)">
              <Input.TextArea rows={3} maxLength={5000} showCount />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
};

export default AppraisalFiveR;
